import {
	AccountInfo,
	AuthenticationResult,
	Configuration,
	InteractionRequiredAuthError,
	PublicClientApplication,
	RedirectRequest,
	SilentRequest,
} from "@azure/msal-browser";
import AUTH_CONFIG from "../../configs/authConfig";
import type { IAuthProvider } from "./types";

const getRedirectUri = (): string => {
	return typeof window !== "undefined" ? window.location.origin : "";
};

const msalConfig: Configuration = {
	auth: {
		clientId: AUTH_CONFIG.clientId,
		redirectUri: getRedirectUri(),
		authority: AUTH_CONFIG.authority,
	},
};

export const msalInstance = new PublicClientApplication(msalConfig);

export const loginRequest: SilentRequest | RedirectRequest = {
	scopes: [AUTH_CONFIG.scope ?? "User.Read"],
};

/**
 * Browser auth provider using MSAL.js (Azure AD authentication).
 *
 * This provider is used when the app runs in a web browser or in a native wrapper
 * with SSO disabled. It implements silent token acquisition with interactive fallback.
 */
class BrowserAuthProvider implements IAuthProvider {
	private msalInstance: PublicClientApplication;

	constructor() {
		this.msalInstance = msalInstance;
	}

	async login(): Promise<void> {
		const request: RedirectRequest = {
			scopes: [AUTH_CONFIG.scope ?? "User.Read"],
			redirectUri: getRedirectUri(),
		};
		await this.msalInstance.loginRedirect(request);
	}

	async logout(): Promise<void> {
		const account = this.getAccount();
		await this.msalInstance.logoutRedirect({
			account: account || undefined,
			postLogoutRedirectUri: getRedirectUri(),
		});
	}

	/**
	 * Acquire an access token.
	 * If no account is available, returns empty string (sign-in required).
	 * Attempts silent acquisition first; if it requires interaction, falls back to interactive login via redirect.
	 */
	async getToken(scopes: string[]): Promise<AuthenticationResult | null> {
		const account = this.getAccount();

		if (!account) {
			return null;
		}

		try {
			// Use provided scopes or fallback to configured scope
			const requestScopes = scopes.length > 0 ? scopes : (AUTH_CONFIG.scope ? [AUTH_CONFIG.scope] : ["User.Read"]);
			const request: SilentRequest = {
				scopes: requestScopes,
				account: account,
			};
			const response = await this.msalInstance.acquireTokenSilent(request);
			return response;
		} catch (error) {
			if (error instanceof InteractionRequiredAuthError) {
				// Silent acquisition failed; fall back to interactive redirect
				const requestScopes = scopes.length > 0 ? scopes : (AUTH_CONFIG.scope ? [AUTH_CONFIG.scope] : ["User.Read"]);
				const redirectRequest: RedirectRequest = {
					scopes: requestScopes,
					redirectUri: getRedirectUri(),
					account: account,
				};
				await this.msalInstance.acquireTokenRedirect(redirectRequest);
				console.warn("Browser auth: Silent token acquisition failed, redirecting for interactive login.");
				return null;
			}
			throw error;
		}
	}

	/**
	 * Handle redirect after authentication (e.g., on app startup).
	 * Restores any cached account from the browser's localStorage/sessionStorage.
	 * Sets the active account if an account was restored.
	 */
	async handleRedirectPromise(): Promise<void> {
		try {
			await this.msalInstance.initialize();
			await this.msalInstance.handleRedirectPromise();

			// After handling the redirect, set the active account if we have any
			const accounts = this.msalInstance.getAllAccounts();
			if (accounts.length > 0) {
				this.msalInstance.setActiveAccount(accounts[0]);
			}
		} catch (error) {
			console.error("Browser auth: handleRedirectPromise failed", error);
			throw error;
		}
	}

	/**
	 * Get the currently active account (for silent token acquisition).
	 */
	private getAccount(): AccountInfo | null {
		const accounts = this.msalInstance.getAllAccounts();
		return accounts.length > 0 ? accounts[0] : null;
	}
}

export { BrowserAuthProvider };
